import type { BinStatus } from '../features/locations/types';

const API_URL = process.env.REACT_APP_API_URL || '/api';
const BASE = `${API_URL}/material-catalog`;

export type StockedFilter = 'all' | 'stocked' | 'unstocked';
export type MaterialField = 'code' | 'description' | 'materialTypeId' | 'unitOfMeasure' | 'owner';

/** A bin holding the material, in a service area the user can see. */
export interface CatalogBin {
  id: string;
  code: string;
  owner: string | null;
  site: { id: string; name: string; number: string | null };
  location: { id: string; name: string; type: string };
  onHand: number;
  minQuantity: number | null;
  maxQuantity: number | null;
  status: BinStatus;
}

export interface CatalogMaterial {
  id: string;
  code: string | null;
  description: string | null;
  type: { id: string; name: string } | null;
  unitOfMeasure: string | null;
  owner: string | null;
  winterMaterial: boolean;
  vehicleRequiredForWithdrawal: boolean;
  siteCount: number;
  locationCount: number;
  binCount: number;
  onHand: number | null;
  bins: CatalogBin[];
}

export interface CatalogPage {
  materials: CatalogMaterial[];
  total: number;
  page: number;
  pageSize: number;
}

export interface CatalogOptions {
  units: string[];
  owners: string[];
  types: { id: string; name: string }[];
}

export interface MaterialPayload {
  code: string;
  description: string;
  materialTypeId: string;
  unitOfMeasure: string;
  owner: string;
  winterMaterial: boolean;
  vehicleRequiredForWithdrawal: boolean;
}

/** A refused save. `fields` puts each message under the field it is about (FORM-01). */
export class CatalogRequestError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly fields: Partial<Record<MaterialField, string>> = {}
  ) {
    super(message);
  }
}

async function readError(response: Response, fallback: string): Promise<CatalogRequestError> {
  const body = (await response.json().catch(() => null)) as { error?: string; fields?: Partial<Record<MaterialField, string>> } | null;
  return new CatalogRequestError(body?.error || fallback, response.status, body?.fields ?? {});
}

async function get<T>(url: string, fallback: string): Promise<T> {
  const response = await fetch(url, { credentials: 'include' });
  if (!response.ok) throw await readError(response, fallback);
  return (await response.json()) as T;
}

async function send<T>(method: 'POST' | 'PATCH', url: string, body: unknown, fallback: string): Promise<T> {
  const response = await fetch(url, {
    method,
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!response.ok) throw await readError(response, fallback);
  return (await response.json()) as T;
}

export const materialCatalogService = {
  list: (query: { search: string; typeIds: string[]; stocked: StockedFilter; page: number; pageSize: number }) => {
    const params = new URLSearchParams({ page: String(query.page), pageSize: String(query.pageSize), stocked: query.stocked });
    if (query.search.trim()) params.set('search', query.search.trim());
    if (query.typeIds.length) params.set('typeIds', query.typeIds.join(','));
    return get<CatalogPage>(`${BASE}?${params.toString()}`, 'Could not load the material catalogue');
  },

  options: () => get<CatalogOptions>(`${BASE}/options`, 'Could not load the catalogue options'),

  codeAvailability: (code: string, excludeId?: string) => {
    const params = new URLSearchParams({ code });
    if (excludeId) params.set('excludeId', excludeId);
    return get<{ available: boolean; message: string | null }>(`${BASE}/code-availability?${params.toString()}`, 'Could not check the code');
  },

  create: (payload: MaterialPayload) => send<CatalogMaterial>('POST', BASE, payload, 'Could not add the material'),

  update: (id: string, payload: MaterialPayload) =>
    send<CatalogMaterial>('PATCH', `${BASE}/${encodeURIComponent(id)}`, payload, 'Could not save the material'),
};
